/*
 * Login component to handle the user login
 * <p>
 *
 * @author lachezar.parvin
 * @author Gallagher Bassett IT
 * @author Copyright (c) 2020 Gallagher Bassett Services, Inc.
 *
 */

import {
  Component,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  AuthResponse,
  AuthService,
  ClientService,
  OidcAuthService,
  UserService,
} from 'app/shared/services';
import {
  ActivatedRoute,
  Router,
} from '@angular/router';
import { ConfigurationService } from 'app/shared/services/configuration.service';
import { ErrorsFormatterUtilService } from 'app/shared/services/errors-formatter-util.service';
import { Constants } from 'app/shared/config/constants';
import { UiConfigService } from 'app/shared/services/ui-config.service';
import {
  extractRedirectPathFromSearch,
  getUrlSegmentKey,
  isHandOffApp
} from 'app/shared/lib';
import { MfaService } from 'app/shared/services/mfa.service';
import {
  GbruSnapshot,
  PostLoginRedirectService
} from 'app/shared/services/post-login-redirect.service';

@Component ( {
  selector: 'reporter-login',
  templateUrl: './login.component.html',
  styleUrls: [ './login.component.scss' ],
  encapsulation: ViewEncapsulation.None,
  standalone: false
} )
export class LoginComponent implements OnInit {
  customerMessageError = '';
  failed = false;
  useLegacy = false;
  showForgotPassword: boolean = false;
  private redirectPath: string | null = null;
  loginType: 'SSO_ONLY' | 'LEGACY_ONLY' | 'SSO_AND_LEGACY' = 'SSO_AND_LEGACY';
  loginForm: FormGroup;
  sso401 = false;
  gbru = localStorage.getItem ( Constants.localStorageKeys.___gbru );
  private previousGbru: GbruSnapshot | null = null;
  hideView = false;
  authRedirectApp: string | null = null;

  constructor (
    private authService: AuthService,
    private oidcService: OidcAuthService,
    private router: Router,
    private route: ActivatedRoute,
    private configService: ConfigurationService,
    private errorFormatter: ErrorsFormatterUtilService,
    private userService: UserService,
    private clientService: ClientService,
    private uiConfigService: UiConfigService,
    private mfaService: MfaService,
    private postLoginRedirectService: PostLoginRedirectService,
  ) {
    this.loginForm = new FormGroup ( {
      userId: new FormControl ( null, [ Validators.required ] ),
      password: new FormControl ( null, [ Validators.required ] ),
    } );
  }

  async ngOnInit () {
    const requestedAuthRedirectApp = this.getRequestedAuthRedirectApp ();
    if ( requestedAuthRedirectApp ) {
      sessionStorage.setItem ( Constants.authRedirectAppStorageKey, requestedAuthRedirectApp );
      if ( !isHandOffApp ( requestedAuthRedirectApp ) ) {
        await this.authService.setAuthenticated ( false );
      }
    }
    this.authRedirectApp = this.getStoredAuthRedirectApp ();
    await this.invalidateAuthIfHandOffRequestRejected ();

    this.loginType = this.configService.byKey ( 'auth.type' );
    this.useLegacy = this.loginType === 'LEGACY_ONLY' || localStorage.getItem ( 'useLegacy' ) === 'true';
    this.redirectPath = this.getRequestedRedirectPath ();

    this.sso401 = this.route.snapshot.params?.error === '401';

    this.previousGbru = this.postLoginRedirectService.parseGbru ( this.gbru );
    if ( /authenticate/.test ( location.pathname ) && this.route.snapshot.queryParams.code ) {
      this.hideView = true;
      this.oidcService.completeLogin ()
        .then ( user => {
          this.redirectPath = this.resolveRedirectPath ( user?.state?.redirectPath ) || this.redirectPath;
          const checkLoginSubscription = this.authService.loginChanged
            .subscribe (
              ( res ) => {
                if ( res ) {
                  checkLoginSubscription.unsubscribe ();
                  this.redirect ();
                }
              },
              ( error ) => {
                checkLoginSubscription.unsubscribe ();
                console.error ( error );
                window.alert ( 'An error occurred during login. Please try again.\n' + ( error?.message || error || 'Unknown error' ) );
                this.redirect ();
              }
            );
          // Add a timeout to avoid hanging if loginChanged never emits
          setTimeout ( () => {
            if ( !checkLoginSubscription.closed ) {
              checkLoginSubscription.unsubscribe ();
              window.alert ( 'Login timed out. Please try again.' );
              this.redirect ();
            }
          }, 10000 ); // 10 seconds
        } )
        .catch ( error => {
          console.error ( error );
          alert ( 'An error occurred during login. Please contact support and provide this code: ' + ( error?.message || error || 'Unknown error' ) );
          this.redirect ();
        } );
    } else if ( this.authService.isAuthenticated && !isHandOffApp ( this.authRedirectApp ) ) {
      this.redirect ();
    }
  }

  async onSubmit () {
    this.failed = false;
    try {
      const handOffApplication = isHandOffApp ( this.authRedirectApp ) ? this.authRedirectApp : undefined;
      const login = await this.authService.login ( this.loginForm.value.userId, this.loginForm.value.password, handOffApplication );
      if ( this.isMfaResponse ( login ) ) {
        this.startMfaFlow ( login );
        return;
      }
      if ( login ) {
        // Unset the useLegacy flag if the user logs in successfully
        localStorage.removeItem ( 'useLegacy' );
        const redirected = await this.redirect ( this.getHandOffToken ( login ), true );
        if ( redirected === false ) {
          this.failed = true;
          this.customerMessageError = 'We could not verify your contact email address. Please try again.';
        }
      }
    } catch ( err ) {
      const customError = this.errorFormatter.formatError ( err ).clientMessage;
      this.failed = true;
      this.customerMessageError = customError;
    }
  }

  private isMfaResponse ( login: boolean | AuthResponse ): login is AuthResponse {
    return !!login && typeof login === 'object' && login.type === 'MFA';
  }

  // Only a login for another app returns the token itself instead of starting a Reporter session
  private getHandOffToken ( login: boolean | AuthResponse | undefined ): string | null {
    return login && typeof login === 'object' ? login.accessToken : null;
  }

  private startMfaFlow ( response: AuthResponse ) {
    if ( !response.accessToken || typeof response.accessToken !== 'string' || !response.accessToken.trim () ) {
      this.failed = true;
      this.customerMessageError = 'Error authenticating, please try again later!';
      return;
    }

    this.mfaService.save ( {
      mfaToken: response.accessToken,
      redirectPath: this.redirectPath,
      returnUrl: this.route.snapshot.queryParams?.returnUrl ?? null,
      urlSegmentKey: this.uiConfigService.urlSegmentKey,
      authRedirectApp: this.authRedirectApp,
    } );

    const redirected = this.mfaService.redirectToMfa ( response.mfaRedirectUrl );

    if ( !redirected ) {
      this.mfaService.clear ();
      this.failed = true;
      this.customerMessageError = 'Error authenticating, please try again later!';
    }
  }

  get isAuthenticated () {
    return this.authService.isAuthenticated;
  }

  oidcLogin () {
    const ssoTid = this.uiConfigService.ssoTid;
    const tidPair = ssoTid ? ssoTid.split ( '=' ) : [];
    if ( !tidPair[ 1 ] ) {
      tidPair[ 1 ] = tidPair[ 0 ];
      tidPair[ 0 ] = 'T';
    }
    const extraParams = ssoTid ? { [ tidPair[ 0 ] ]: tidPair[ 1 ] } : {};
    const state = {
      ...( this.redirectPath ? { redirectPath: this.redirectPath } : {} ),
      ...( this.uiConfigService.urlSegmentKey ? { urlSegmentKey: this.uiConfigService.urlSegmentKey } : {} ),
    };

    this.oidcService.login ( extraParams, Object.keys ( state ).length ? state : undefined );
  }

  private redirect ( handOffToken?: string | null, credentialsLogin = false ): Promise<boolean> {
    const stringAfterReturnUrl: RegExpExecArray = /returnUrl=(.*)/.exec ( decodeURIComponent ( this.router.url ) );

    return this.postLoginRedirectService.redirect ( {
      gbru: this.previousGbru,
      redirectPath: this.redirectPath,
      returnUrl: stringAfterReturnUrl?.[ 1 ] ?? null,
      authRedirectApp: this.authRedirectApp,
      ...( handOffToken ? { handOffToken } : {} ),
      ...( credentialsLogin ? { credentialsLogin } : {} ),
    } );
  }

  private getRequestedAuthRedirectApp (): string | null {
    const app = this.route.snapshot.queryParams?.[ Constants.authRedirectAppParam ];
    const landingUrlKey = Constants.authRedirectAppLandingUrlKeys.get ( app );

    if ( !landingUrlKey || !this.isSameOrigin ( document.referrer, this.configService.byKey ( landingUrlKey ) ) ) {
      return null;
    }

    return app;
  }

  private getStoredAuthRedirectApp (): string | null {
    const storedApp = sessionStorage.getItem ( Constants.authRedirectAppStorageKey );
    const requestedApp = this.route.snapshot.queryParams?.[ Constants.authRedirectAppParam ];

    if ( isHandOffApp ( storedApp ) && requestedApp !== storedApp ) {
      sessionStorage.removeItem ( Constants.authRedirectAppStorageKey );
      return null;
    }

    return storedApp;
  }

  // initUiConfig skips its session check for hand-off requests, so a rejected request gets it here
  private async invalidateAuthIfHandOffRequestRejected () {
    const requestedApp = this.route.snapshot.queryParams?.[ Constants.authRedirectAppParam ];

    if ( isHandOffApp ( requestedApp ) && !isHandOffApp ( this.authRedirectApp ) && !getUrlSegmentKey () ) {
      await this.authService.invalidateAuthIfUiConfigMismatch ( null );
    }
  }

  private isSameOrigin ( url: string, otherUrl: string ): boolean {
    try {
      return new URL ( url ).origin === new URL ( otherUrl ).origin;
    } catch ( e ) {
      return false;
    }
  }

  private resolveRedirectPath ( redirectPath?: string ): string | null {
    const resolvedRedirectPath = redirectPath?.trim ();

    if ( !resolvedRedirectPath ||
      !resolvedRedirectPath.startsWith ( '/' ) ||
      resolvedRedirectPath.startsWith ( '//' ) ||
      /^[a-zA-Z][a-zA-Z\d+.-]*:/.test ( resolvedRedirectPath ) ||
      /^\/(?:login|authenticate)(?:\/|$)/.test ( resolvedRedirectPath ) ) {
      return null;
    }

    return resolvedRedirectPath;
  }

  private getRequestedRedirectPath (): string | null {
    const routeRedirectPath = this.route.snapshot.queryParams?.redirectPath;

    if ( routeRedirectPath == null ) {
      return null;
    }

    // Preserve nested query params for custom login URLs such as
    // /{urlSegmentKey}/login?redirectPath=/incident/123/properties?tab=summary&mode=edit.
    // Angular query param parsing splits the inner `&...` params, so prefer the raw search value.
    const rawRedirectPath = extractRedirectPathFromSearch ( location.search );
    return this.resolveRedirectPath ( rawRedirectPath ?? routeRedirectPath );
  }

  get currentUser () {
    return this.userService.currentUser;
  }

  get uiConfig () {
    return this.uiConfigService;
  }

  get mfaNoticeEnabled (): boolean {
    return this.configService.mfaNoticeEnabled;
  }

  get showMfaNotice (): boolean {
    if ( !this.mfaNoticeEnabled ) {
      return false;
    }
    return this.loginPageVersion === 'CUSTOM_UI_CONFIG'
      ? this.uiConfig.loginType === 'LEGACY'
      : this.useLegacy && !this.sso401;
  }

  get loginPageVersion (): string {
    if ( this.uiConfig.isCustom ) {
      return 'CUSTOM_UI_CONFIG';
    } else {
      return 'DEFAULT';
    }
  }
}
